// Best-effort guesser for the three NIQ Fragrances import tags (gender, dimension
// type, dimension value) from a file's name. NIQ exports are named in a fairly
// consistent way, e.g.:
//
//   "Male Sales Value (Central Java + DIY).xlsx" -> male / area / "Central Java + DIY"
//   "Female Sales Value (National).xlsx"         -> female / summary / "National"
//   "Male Sales Value (Minimarket).xlsx"         -> male / channel / "Minimarket"
//
// The result is only ever used to PRE-FILL the form — the admin can still change
// any field before importing — so a wrong guess is harmless. Every guess is
// validated against the server-provided option lists; anything that doesn't map
// to a known option is left blank.

export type InferredNiqTags = {
  gender: string
  dimension_type: string
  dimension_value: string
}

// Keyword groups for gender. "female" is checked before "male" because "male"
// is a substring of "female" — order matters even with word boundaries.
const GENDER_KEYWORDS: { value: string; pattern: RegExp }[] = [
  { value: "female", pattern: /\b(female|wanita|women|perempuan)\b/i },
  { value: "male", pattern: /\b(male|pria|men|laki)\b/i },
]

// Keyword groups for dimension type, checked in priority order: a "National"
// summary file may also mention a region, so summary wins; channel beats area
// for the same reason (a channel can carry a region name in other columns).
// "Indonesia" / "National" denote the total universe (e.g. "Indonesia Urban"),
// so they are summary — and are checked BEFORE the channel rule so the trailing
// "Urban" doesn't misclassify the national total as a channel breakdown.
const SUMMARY_PATTERN = /\b(national|nasional|indonesia|summary|ringkasan|total)\b/i

// In these NIQ exports the "Urban"/"Rural" split is always a channel breakdown,
// so that token alone identifies a channel value (catches MTI/H&B/HySu/MM Urban
// and any new channel code without enumerating every abbreviation). "urban"/
// "rural" are matched as substrings, not whole words, because NIQ sometimes
// concatenates them ("HySuUrban") with no separating space.
const CHANNEL_PATTERN =
  /(urban|rural|\bminimarket\b|\bhypermarket\b|\bsupermarket\b|\bgrocery\b|\bprovision\b|\bconvenience\b|\bwholesale\b|\bgrosir\b|\bchannel\b|\bcosmetic\b|\bpharmacy\b|\bapotek\b|\bdrug\s*store\b|\bh\s*&\s*b\b|\bhealth\s*&?\s*beauty\b|\bhysu\b|\bmodern\s*trade\b|\bgeneral\s*trade\b|\bmt\b|\bgt\b|\bmm\b)/i

const AREA_PATTERN =
  /\b(area|region|island|pulau|java|jawa|jabar|jateng|jatim|jabodetabek|sumatera|sumatra|kalimantan|sulawesi|bali|papua|maluku|nusa\s*tenggara|\bntt\b|\bntb\b|\bdiy\b|yogya|jakarta|banten|greater)\b/i

// Pull the dimension value out of the last "(...)" group in the filename. NIQ
// names put the dimension in parentheses; if there are none, we can't guess it.
function extractParenValue(base: string): string {
  const matches = [...base.matchAll(/\(([^)]+)\)/g)]
  if (matches.length === 0) return ""
  return matches[matches.length - 1][1].trim()
}

export function inferNiqFragranceTags(
  filename: string,
  genderOptions: string[],
  dimensionTypeOptions: string[],
  // Map of previously-imported dimension value (lowercased) -> dimension type.
  // Human-confirmed history beats keyword heuristics, so it is checked first.
  knownDimensionTypes: Record<string, string> = {},
): InferredNiqTags {
  // Strip the extension so it never interferes with keyword matching.
  const base = filename.replace(/\.[^.]+$/, "")

  const dimension_value = extractParenValue(base)

  // Gender: match against the filename, then keep it only if it's a real option.
  let gender = ""
  for (const { value, pattern } of GENDER_KEYWORDS) {
    if (pattern.test(base) && genderOptions.includes(value)) {
      gender = value
      break
    }
  }

  // Dimension type. First trust history: if this exact value was tagged before,
  // reuse that type. Otherwise fall back to keyword heuristics on the
  // parenthetical value (or the whole name). Priority: summary > channel > area.
  let dimension_type = ""
  const remembered = dimension_value ? knownDimensionTypes[dimension_value.toLowerCase()] : undefined
  if (remembered) {
    dimension_type = remembered
  } else {
    const haystack = dimension_value || base
    if (SUMMARY_PATTERN.test(haystack)) dimension_type = "summary"
    else if (CHANNEL_PATTERN.test(haystack)) dimension_type = "channel"
    else if (AREA_PATTERN.test(haystack)) dimension_type = "area"
  }
  if (dimension_type && !dimensionTypeOptions.includes(dimension_type)) {
    dimension_type = ""
  }

  return { gender, dimension_type, dimension_value }
}
